import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { produce } from 'immer';
import { useShallow } from 'zustand/react/shallow';
import classNames from 'classnames';

import i18n from '@nextroll/ar-i18n';
import { Placements, Popper, Tooltip } from '@nextroll/ar-style-base';
import { useContainerContext } from '@nextroll/ar-container-context';

import { ActionNodeBase, INodeBasePanelElementConfig, INodeBaseProps } from 'components/basic/NodeBase/NodeBase';
import { NodeHeaderSection } from 'components/basic/NodeHeaderSection/NodeHeaderSection';
import { NodeAuthSection } from 'components/basic/NodeAuthSection/NodeAuthSection';
import {
    ActionConfigValues,
    ActionMetadata,
    IPropertyError,
    IWorkflowSchemaNode,
    NodeIssueCode,
    TrayServiceType,
    WORKFLOW_NODE,
} from 'types/rofl';
import { isAuthError } from 'util/util';
import { generatePropertyErrorKey } from 'util/errors';
import { useRoflStore } from 'store/roflStore';
import { SERVICES } from 'util/services.constants';
import { WorkflowAction, WorkflowActionService } from 'types/roflWorkflowActions';
import useRequest from 'hooks/useRequest';
import { NodeRecordFields, WorkflowActionNode } from 'store/nodeSlice';

import { ActionNodePanel } from './ActionNodePanel/ActionNodePanel';
import { ActionNodeEmailPanel, ActionNodeEmailPanelHeader } from './ActionNodePanel/ActionNodeEmailPanel';
import useGeneratePropertyInputs from './generatePropertyInput/useGeneratePropertyInputs';
import useGenerateRecordFields from './generatePropertyInput/useGenerateRecordFields';

export interface IActionNodeProps {
    id: string;
    actionEid: string;
    workflowActionServices: WorkflowActionService[];
    loadingWorkflow: boolean;
}
export const ActionNode = (props: INodeBaseProps<IWorkflowSchemaNode> & IActionNodeProps) => {
    const { advertisable: { eid: advertisableEid } = {} } = useContainerContext();
    const [requestAuthError, setRequestAuthError] = useState<NodeIssueCode | undefined>();
    const [showTextOverflowPopover, setShowTextOverflowPopover] = useState<boolean>(false);
    const nodeTextElementRef = useRef<HTMLDivElement>(null);

    const [
        node,
        authentications,
        updateAuthentications,
        loadingAuth,
        removeNodeData,
        updateRecordFields,
    ] = useRoflStore(useShallow(state => [
        state.contentData[props.id] as WorkflowActionNode,
        state.authentications,
        state.updateAuthentications,
        state.loadingAuth,
        state.removeNodeData,
        state.updateRecordFields,
    ]));

    const initialContent = useMemo(() => node?.data && {
        ...node?.data,
        error: props.validationErrors?.some(error => error.node_eid === props.id),
    }, [node?.data, props.validationErrors, props.id]);

    // local data; 'original' for previous changes vs. 'current' for unsaved changes
    const [workflow, setWorkflow] = useState({
        original: initialContent,
        current: initialContent,
    } as { original?: IWorkflowSchemaNode; current?: IWorkflowSchemaNode } | undefined);
    const [recordFields, setRecordFields] = useState({
        original: node.recordFields,
        current: node.recordFields,
    } as { original?: NodeRecordFields; current?: NodeRecordFields } | undefined);

    const [forceSlidingModalShow, setForceSlidingModalShow] = useState<boolean>();

    const [inputPropertiesCall, inputPropertiesLoading] = useRequest();
    const [generateRecordsCall, generateRecordsLoading] = useRequest();

    const { workflowActionServices, onApply, onClose, actionEid } = props;
    const [workflowActionService, setWorkflowActionService] = useState<WorkflowActionService | undefined>();
    const [selectedWorkflowAction, setSelectedWorkflowAction] = useState<WorkflowAction | undefined>();

    const currentWorkflowActionEid = selectedWorkflowAction?.eid;
    const serviceName = workflowActionService?.service_name;
    const serviceTitle = workflowActionService?.service_display_name;
    const actionTitle = selectedWorkflowAction?.display_name || '';
    const serviceIcon = serviceName ? SERVICES[serviceName]?.icon : '';

    // selected emails for the email notification node, if exists
    const isEmailNodeSelected = currentWorkflowActionEid === 'NVB4QOSPFBBPBEL4LV8RFL'; // email node action EID
    const selectedEmails = useMemo(
        () => (workflow?.current || workflow?.original)?.action_config?.config?.emails as string[] || [],
        [workflow],
    );

    // this controls whether the panel shows with workflow action properties (after you select an action)
    const [showWorkflowActionPanelProperties, setShowWorkflowActionProperties] = useState<boolean>(
        !!currentWorkflowActionEid
    );

    // if we don't have a selectedTrayOperation but a passed in operationEid,
    // we can set it programmatically instead of through the UI.
    useEffect(() => {
        if (!selectedWorkflowAction && actionEid) {
            for (const actionService of workflowActionServices) {
                for (const workflowAction of actionService.workflow_actions) {
                    if (workflowAction.eid === actionEid) {
                        setWorkflowActionService(actionService);
                        setSelectedWorkflowAction(workflowAction);
                        setShowWorkflowActionProperties(true);
                        return;
                    }
                }
            }
        }
    }, [actionEid,
        currentWorkflowActionEid,
        selectedWorkflowAction,
        workflowActionServices]);

    // detect external validation error of the current node
    const nodeValidationError = props.validationErrors?.find(error => error.node_eid === props.id);

    // convert property errors in this node into a map
    const propertyErrors = nodeValidationError?.property_errors && nodeValidationError.property_errors.reduce(
        (errors, propertyError) => ({
            ...errors,
            [generatePropertyErrorKey(propertyError.property_name, propertyError.record_field)]: propertyError,
        }), {}) as Record<string, IPropertyError>;

    /**
     * Determine whether or not the service auth is granted.
     */
    const serviceAuthenticationGranted = useMemo(
        () => ((authentications[serviceName as TrayServiceType]?.name)
            && (!props.loadingWorkflow
            && (!(nodeValidationError?.error && isAuthError(nodeValidationError.error)))
            && !requestAuthError)),
        [authentications, serviceName, props.loadingWorkflow, nodeValidationError?.error, requestAuthError]
    );

    /**
     * Determine whether or not the service has any missing scopes.
     */
    const missingScopes = useMemo(
        () => authentications[serviceName as TrayServiceType]?.missing_scopes?.length > 0,
        [authentications, serviceName]);
    const authedWithoutMissingScopes = serviceAuthenticationGranted && !missingScopes;

    /**
     * Update realtime data of the action node.
     */

    const getDefaultWorkflowData = useCallback((): IWorkflowSchemaNode => ({
        eid: props.id || 'Node-1',
        action_config: {
            action_eid: currentWorkflowActionEid || '',
        },
        type: WORKFLOW_NODE.ACTION,
        next: undefined
    }), [props.id, currentWorkflowActionEid]);

    const handleUpdateWorkflow = useCallback(
        (
            config: ActionConfigValues,
            metadata?: ActionMetadata,
        ) => setWorkflow(previousWorkflow => {
            const updatedWorkflow = getDefaultWorkflowData();
            updatedWorkflow.action_config = {
                ...updatedWorkflow.action_config,
                config,
                metadata: metadata ?? previousWorkflow?.current?.action_config?.metadata,
            };

            return {
                ...previousWorkflow,
                current: updatedWorkflow
            };
        }), [getDefaultWorkflowData]);

    const handleUpdateRecordFields = useCallback(
        (newRecordFields: NodeRecordFields) => setRecordFields(previousRecordFields => {
            if (!previousRecordFields?.original) {
                return {
                    original: newRecordFields,
                    current: newRecordFields
                };
            }
            return {
                ...previousRecordFields,
                current: newRecordFields
            };
        }), []);

    /**
     * update Authentication Error based on response from API
     */
    const handleRequestError = useCallback((error?: any) => {
        if (error?.parsedBody?.error && isAuthError(error.parsedBody.error)) {
            setRequestAuthError(error.parsedBody.error);
            return true;
        }
        return false;
    }, []);

    // Handle resolving dependencies and populating record definitions.
    useGenerateRecordFields({
        workflowAction: selectedWorkflowAction,
        handleUpdateRecordFields,
        handleUpdateWorkflowNode: handleUpdateWorkflow,
        recordFields: recordFields?.current,
        showWorkflowActionPanelProperties,
        currentWorkflowActionConfig: workflow?.current?.action_config,
        originalWorkflowActionConfig: workflow?.original?.action_config,
        call: generateRecordsCall,
        handleRequestError,
        propertyErrors: workflow?.original?.error ? propertyErrors : undefined,
    });

    const { workflowPropertyInputs, propertyInputText } = useGeneratePropertyInputs({
        nodeId: props.id,
        workflowActionService,
        workflowAction: selectedWorkflowAction,
        workflow,
        call: inputPropertiesCall,
        handleUpdateWorkflowNode: handleUpdateWorkflow,
        propertyErrors: workflow?.original?.error ? propertyErrors : undefined,
        loadingWorkflow: props.loadingWorkflow,
        authedWithoutMissingScopes,
        handleRequestError,
        recordFields: recordFields?.current,
    });

    const handleClose = useCallback(() => {
        // clear the action config values if canceling. We are overriding the default
        // handleClose behavior slightly so we return false.

        setRecordFields(produce(draftRecordField => {
            if (draftRecordField?.current && draftRecordField?.original) {
                draftRecordField.current = draftRecordField.original;
            } else if (draftRecordField?.current) {
                draftRecordField.current = undefined;
            }


            /*
            * TODO: make the recordField error handling a bit better.
            * This block will re-trigger definition calls on error, but probably needs
            * to be more robust at some point.
            */
            for (const recordFieldsKeys of [draftRecordField?.current || {}, draftRecordField?.original || {}]) {
                for (const recordField of Object.entries(recordFieldsKeys)) {
                    // records that have keys but no values would be from an error
                    if (!recordField[1].fields) {
                        delete draftRecordField?.current?.[recordField[0]];
                    }
                }
            }
        }));

        setWorkflow(produce(draftWorkflow => {
            if (draftWorkflow?.current?.action_config && draftWorkflow?.original?.action_config) {
                draftWorkflow.current.action_config = draftWorkflow?.original?.action_config;
            } else if (draftWorkflow?.current) {
                draftWorkflow.current.action_config = getDefaultWorkflowData().action_config;
            }
        }));


        onClose?.();
        return false;
    }, [getDefaultWorkflowData, onClose]);


    const actionNodePanel = useCallback(() => (
        <ActionNodePanel
            workflowActionServices={workflowActionServices}
            presetWorkflowAction={selectedWorkflowAction}
            setWorkflowActionService={setWorkflowActionService}
            setWorkflowAction={setSelectedWorkflowAction}
            onClose={handleClose}
        />
    ), [handleClose, selectedWorkflowAction, workflowActionServices]);

    const actionNodeEmailPanel = useCallback(() => (
        <ActionNodeEmailPanel
            advertisableEid={advertisableEid}
            emails={selectedEmails}
            onUpdateEmails={emails => handleUpdateWorkflow({ emails })}
        />
    ), [advertisableEid, selectedEmails, handleUpdateWorkflow]);

    const actionNodeHeaderSection = useCallback(() => (
        serviceTitle && actionTitle && selectedWorkflowAction?.eid
            ? (
                <NodeHeaderSection
                    icon={serviceIcon}
                    serviceTitle={serviceTitle}
                    heading={actionTitle}
                    actionEid={selectedWorkflowAction.eid}
                />
            ) : null
    ), [serviceTitle, actionTitle, serviceIcon, selectedWorkflowAction]);

    const authElement = useCallback((entityTitle: string) => (
        serviceTitle ? (
            <NodeAuthSection
                advertisableEid={advertisableEid}
                hasAuthenticationGranted={serviceAuthenticationGranted}
                updateAuthentications={updateAuthentications}
                missingScopes={missingScopes}
                validationError={requestAuthError || nodeValidationError?.error}
                serviceTitle={serviceTitle}
                entityTitle={entityTitle}
            />
        ) : null
    ), [
        serviceTitle,
        advertisableEid,
        serviceAuthenticationGranted,
        updateAuthentications,
        missingScopes,
        nodeValidationError?.error,
        requestAuthError
    ]);

    /**
     * List of elements that will be rendered in the action node panel.
     */
    const elementConfigs = !showWorkflowActionPanelProperties
        ? [{ key: '', component: actionNodePanel }]
        : [
            { key: '', component: isEmailNodeSelected ? ActionNodeEmailPanelHeader : actionNodeHeaderSection },
            // properties from UI schema
            ...((authedWithoutMissingScopes && !isEmailNodeSelected) ? workflowPropertyInputs : []),
            // email notification
            isEmailNodeSelected && {
                key: 'workload-service-node-panel-email',
                component: actionNodeEmailPanel,
                validate: () => true,
            },
            // auth element if permissions are missing
            !authedWithoutMissingScopes && !isEmailNodeSelected && {
                key: 'workload-service-node-panel-element-auth',
                component: () => authElement(actionTitle),
                validate: () => false, // do not proceed if auth is incompleted
            },
        ].filter(Boolean) as INodeBasePanelElementConfig[];

    // update the current node error state based on the validations of the rendered elements
    const areElementConfigsInvalid = elementConfigs.some(config => config.validate?.() === false);
    useEffect(() => {
        setWorkflow(previousWorkflow => ({
            ...previousWorkflow,
            current: previousWorkflow?.current && {
                ...previousWorkflow?.current,
                error: areElementConfigsInvalid,
            } as IWorkflowSchemaNode,
        }));
    }, [areElementConfigsInvalid]);

    // cleanup timeout refs on component unmount
    const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
    useEffect(() => {
        return () => {
            for (const timeoutRef of timeoutRefs.current) {
                clearTimeout(timeoutRef);
            }
            timeoutRefs.current = [];
        };
    }, []);

    const handleApply = useCallback(() => {
        let shouldUpdateContextData = false;

        if (selectedWorkflowAction && !showWorkflowActionPanelProperties) {
            // if we have selected the operation, and we have not yet shown the field configuration panel,
            // set the default workflow data and show the next panel section.
            setWorkflow(previousWorkflow => ({
                ...previousWorkflow,
                current: getDefaultWorkflowData(),
            }));
            setForceSlidingModalShow(false);

            // HACK: without these setTimeouts the react-modal does not mount/unmount properly
            // this is only visible in the tests
            timeoutRefs.current.push(setTimeout(() => {
                timeoutRefs.current.push(setTimeout(() => {
                    // don't set state / memory leak when unmounted
                    setForceSlidingModalShow(undefined);
                    setShowWorkflowActionProperties(true);
                }, 300));
            }));
        } else {
            // if we haven't selected an action, remove any node data if it exists
            if (!selectedWorkflowAction) {
                setWorkflow(previousWorkflow => ({
                    ...previousWorkflow,
                    current: undefined,
                }));
            } else {
                // "final apply", mark the node as complete in the context
                shouldUpdateContextData = true;
                updateRecordFields(props.id, recordFields?.current);
                setRecordFields({ ...recordFields, original: recordFields?.current });
                setWorkflow(previousWorkflow => ({
                    original: previousWorkflow?.current,
                    current: previousWorkflow?.current,
                }));
            }
            onApply?.();
        }
        return shouldUpdateContextData;
    }, [getDefaultWorkflowData, onApply, props.id, recordFields, selectedWorkflowAction,
        showWorkflowActionPanelProperties, updateRecordFields]);

    const classes = useMemo(() => classNames('workflow-node-inner-card', {
        active: !!props.active, // when the node is selected
        error: !!props.error, // when the node is in error state
        warning: !!props.warning && !props.error, // when the node is in warning state
    }), [props.active, props.error, props.warning]);

    // this useEffect is used to determine if the text in the node is overflowing
    useEffect(() => {
        let overflowingText = false;
        let scrollWidth;
        if (nodeTextElementRef.current && propertyInputText) {
            // set scrollWidth to either the outer nodeTextElement or the popper element to get the correct width
            // depending on if the popover is open or not
            scrollWidth = nodeTextElementRef.current.children[0].scrollWidth || nodeTextElementRef.current.scrollWidth;
            if (scrollWidth > nodeTextElementRef.current.clientWidth) {
                overflowingText = true;
            }
        }

        if (!showTextOverflowPopover && overflowingText) {
            setShowTextOverflowPopover(true);
        } else if (showTextOverflowPopover && !overflowingText) {
            setShowTextOverflowPopover(false);
        }
    }, [
        showTextOverflowPopover,
        propertyInputText
    ]);

    return (
        <ActionNodeBase<IWorkflowSchemaNode>
            {...props}
            panelElementConfigs={elementConfigs}
            panelClassName='workflow-service-action-node-panel'
            loading={loadingAuth || generateRecordsLoading}
            preventApply={inputPropertiesLoading || generateRecordsLoading || loadingAuth}
            content={workflow}
            setContent={setWorkflow}
            error={!!workflow?.original?.error || props.error}
            onApply={handleApply}
            onClose={handleClose}
            forceSlidingModalShow={forceSlidingModalShow}
        >
            <div className={classes}>
                {(props.error && (
                    <div className='workflow-node-card-error'>
                        <i className='ar-icon ar-icon-fw fa-exclamation-circle' />
                    </div>
                ))
                || (props.warning && (
                    <div className='workflow-node-card-warning'>
                        <i className='ar-icon ar-icon-fw fa-exclamation-triangle text-warning' />
                    </div>
                ))}
                <div className='workflow-service-action-node-title-container'>
                    {serviceIcon && <img src={serviceIcon} alt='icon' height={20} width={20} />}
                    {serviceTitle && actionTitle && `${serviceTitle}: ${i18n.gettext(actionTitle)}`}
                </div>
                {actionTitle && !node.isIncomplete && (
                    <div className='workflow-service-action-node-text-container' ref={nodeTextElementRef}>
                        {showTextOverflowPopover ? (
                            <Popper
                                hoverContent={
                                    <Tooltip placement={Placements.BOTTOM_END}>{propertyInputText}</Tooltip>
                                }
                            >
                                <strong>{propertyInputText}</strong>
                            </Popper>
                        ) : (
                            <strong>
                                {isEmailNodeSelected
                                    ? i18n.gettext('{{ count }} user(s)', { count: selectedEmails.length })
                                    : propertyInputText}
                            </strong>
                        )}
                    </div>
                ) }
                <div className='workflow-service-action-node-link-container'>
                    <a onClick={() => {}} type='button'>
                        { node.isIncomplete ? i18n.gettext('Set Up') : i18n.gettext('Edit')}
                    </a>
                    <a
                        onClick={ (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
                            // stop propagation to prevent this node from becoming active
                            event.stopPropagation();

                            // reset data
                            removeNodeData(props.id);
                            setWorkflow({
                                original: undefined,
                                current: undefined,
                            });
                            setRecordFields({
                                original: undefined,
                                current: undefined,
                            });
                            setSelectedWorkflowAction(undefined);
                            setShowWorkflowActionProperties(false);
                        }}
                        type='button'
                    >
                        {i18n.gettext('Remove')}
                    </a>
                </div>
            </div>
        </ActionNodeBase>
    );
};
